'use client'

import styled from 'styled-components'

import { Carat, TextLink, TypeStyle } from '@syconium/little-miss-figgy'

import { TriggerRow } from '../../../components/nav/NavOverlayAccordion/styles'
import { NavSubMenuDialog } from '../../../components/nav/NavSubMenuDialog'
import {
  DisclosureList,
  DisclosureListItem,
  DisclosureListItemLink,
} from '../disclosures/DisclosureList.client'
import { NextLink } from '../navigation/NextLink'

import { PrimaryNavContentfulSection } from './PrimaryNavContentfulSections.client'
import {
  PrimaryNavItem,
  PrimaryNavItemItemBadge,
  PrimaryNavItemItemIcon,
} from './PrimaryNavItemListSection.client'
import { useVisibleNavAccordionSections } from './useVisibleNavAccordionSections.client'

import type { NavAccordionSectionFragmentFragment } from '../../../__generated__/graphql/catalog/graphql'

const TriggerCaret = styled(Carat).attrs({ strokeWidth: 1 })`
  height: 15px;
  width: 15px;
  margin-inline-end: ${o => o.theme.dimensions.layout.sidePadding.md}px;
`

const SubNavLinkList = styled.div`
  && ${PrimaryNavItem} {
    ${TypeStyle.css.bodyLarge}
    font-weight: 500;
  }
`

export const PrimaryNavOverlayAccordionSection = ({
  backText,
  closeText,
  onCloseNav,
  openSubMenuAriaLabel,
  sections,
}: NavAccordionSectionFragmentFragment & {
  backText: string
  closeText: string
  onCloseNav: () => void
  openSubMenuAriaLabel: string
}) => {
  const visibleSections = useVisibleNavAccordionSections(sections)

  return (
    <DisclosureList>
      {visibleSections.map(section => {
        const { badge, badgeColor, badgeTextColor, href, icon, text, textColor } = section.header

        const navItem = (
          <PrimaryNavItem style={{ color: textColor ?? undefined }} asTag='span' $variant='heading'>
            {icon ? (
              <PrimaryNavItemItemIcon
                alt=''
                src={icon}
                aspectRatios={{ sm: 1, md: 1 }}
                widths={{ unit: 'px', sm: 16, md: 16 }}
                role='presentation'
                transparentBackground={true}
                loading='lazy'
              />
            ) : null}
            <span>{text}</span>
            {badge ? (
              <PrimaryNavItemItemBadge
                asTag={'span'}
                $badgeColor={badgeColor}
                $badgeTextColor={badgeTextColor}
              >
                {badge}
              </PrimaryNavItemItemBadge>
            ) : null}
          </PrimaryNavItem>
        )

        if (
          section.body?.__typename === 'NavListSection' ||
          section.body?.__typename === 'NavTileSection'
        ) {
          const body = section.body

          return (
            <DisclosureListItem key={section.id}>
              <NavSubMenuDialog
                backLabel={backText}
                closeLabel={closeText}
                onCloseNav={onCloseNav}
                title={text}
                trigger={
                  <TriggerRow
                    $hasPadding
                    aria-label={openSubMenuAriaLabel.replace('{{menuName}}', text)}
                  >
                    {navItem}
                    <TriggerCaret aria-hidden='true' />
                  </TriggerRow>
                }
              >
                <SubNavLinkList>
                  <PrimaryNavContentfulSection {...body} />
                </SubNavLinkList>
              </NavSubMenuDialog>
            </DisclosureListItem>
          )
        }

        if (href) {
          return (
            <DisclosureListItem key={section.id}>
              <DisclosureListItemLink as='div'>
                <TextLink href={href} as={NextLink} variant='underline-on-hover' inheritTypography>
                  {navItem}
                </TextLink>
              </DisclosureListItemLink>
            </DisclosureListItem>
          )
        }

        return null
      })}
    </DisclosureList>
  )
}
